/**
 * Move pre–2026 quiz registrations into a separate archive collection
 * so analytics only sees current-season documents in the live collection.
 */

import {
  collection,
  doc,
  getDocs,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { normalizeSchool } from "./aggregateAnalytics";
import {
  QUIZ_COLLECTION,
  QUIZ_ARCHIVE_COLLECTION,
  QUIZ_SEASON_CUTOFF,
} from "./quizSeason";

const BATCH_LIMIT = 400;

function isStaleQuizDoc(data) {
  const rawTs = data.submittedAt ?? data.timestamp ?? null;
  const at = rawTs?.toDate?.() ?? null;
  if (!at || Number.isNaN(at.getTime())) return true;
  return at < QUIZ_SEASON_CUTOFF;
}

function docMatchesSchoolKeys(data, schoolKeys) {
  const nameKey = normalizeSchool(data.schoolName);
  return schoolKeys.has(nameKey);
}

/**
 * All normalized keys that belong to a canonical school (aliases included).
 * @param {string} schoolKey
 * @param {import('./schoolAliases').AliasGroup[]} aliasGroups
 */
export function schoolKeysForGroup(schoolKey, aliasGroups = []) {
  const keys = new Set([normalizeSchool(schoolKey)].filter(Boolean));
  for (const g of aliasGroups) {
    const groupKey = normalizeSchool(g.canonicalName) || g.id;
    if (groupKey !== schoolKey) continue;
    keys.add(groupKey);
    for (const k of g.memberKeys || []) {
      const n = normalizeSchool(k);
      if (n) keys.add(n);
    }
  }
  return keys;
}

/**
 * Copy stale quiz docs for one school to archive and delete from live collection.
 * @returns {Promise<number>} number of documents archived
 */
export async function archiveStaleQuizForSchool(schoolKey, aliasGroups = []) {
  const schoolKeys = schoolKeysForGroup(schoolKey, aliasGroups);
  if (schoolKeys.size === 0) return 0;

  const snap = await getDocs(collection(db, QUIZ_COLLECTION));
  const toArchive = snap.docs.filter(
    (d) => isStaleQuizDoc(d.data()) && docMatchesSchoolKeys(d.data(), schoolKeys)
  );

  if (toArchive.length === 0) return 0;

  let archived = 0;
  for (let i = 0; i < toArchive.length; i += BATCH_LIMIT) {
    const chunk = toArchive.slice(i, i + BATCH_LIMIT);
    const batch = writeBatch(db);
    for (const d of chunk) {
      const data = d.data();
      batch.set(doc(db, QUIZ_ARCHIVE_COLLECTION, d.id), {
        ...data,
        archivedAt: serverTimestamp(),
        archivedFrom: QUIZ_COLLECTION,
        archiveSeason: "2025",
        canonicalSchoolKey: schoolKey,
      });
      batch.delete(d.ref);
      archived++;
    }
    await batch.commit();
  }

  return archived;
}

/**
 * Archive every stale quiz doc still in the live collection (admin repair).
 * @returns {Promise<number>}
 */
export async function archiveAllStaleQuizRegistrations() {
  const snap = await getDocs(collection(db, QUIZ_COLLECTION));
  const toArchive = snap.docs.filter((d) => isStaleQuizDoc(d.data()));
  if (toArchive.length === 0) return 0;

  let archived = 0;
  for (let i = 0; i < toArchive.length; i += BATCH_LIMIT) {
    const chunk = toArchive.slice(i, i + BATCH_LIMIT);
    const batch = writeBatch(db);
    for (const d of chunk) {
      const data = d.data();
      batch.set(doc(db, QUIZ_ARCHIVE_COLLECTION, d.id), {
        ...data,
        archivedAt: serverTimestamp(),
        archivedFrom: QUIZ_COLLECTION,
        archiveSeason: "2025",
        canonicalSchoolKey: normalizeSchool(data.schoolName) || "",
      });
      batch.delete(d.ref);
      archived++;
    }
    await batch.commit();
  }

  return archived;
}
